import { Flag, Check, Clock, ArrowRight, CheckCircle } from "lucide-react";
import { formatDate } from "~/helpers/date";
import { cn } from "~/lib/utils";
import { type LucideIcon } from "lucide-react";
import { useTranslation } from "react-i18next";
import { WorkflowStepStatuses, type WorkflowStepStatus } from "~/api/types";

interface TimelineIndicatorProps {
  icon: LucideIcon;
  text?: string;
  variant?: "primary" | "secondary";
}

export function TimelineIndicator({
  icon: Icon,
  text,
  variant = "primary",
}: TimelineIndicatorProps) {
  return (
    <>
      <div
        className={cn(
          "bg-card z-10 flex size-8 items-center justify-center justify-self-center rounded-full border-2 shadow-md",
          variant === "primary" ? "border-primary/40" : "border-border/40",
        )}
      >
        <Icon
          className={cn(
            "size-4",
            variant === "primary" ? "text-primary" : "text-muted-foreground",
          )}
        />
      </div>
      <div className="text-muted-foreground flex items-center text-sm">
        {text}
      </div>
    </>
  );
}

export function TimelineStart({ startedAt }: { startedAt?: string | null }) {
  const { t } = useTranslation();

  let text = "";
  if (startedAt) {
    text = `${t(($) => $.commons.workflow.startedAt)} ${formatDate(startedAt, "date")}`;
  }

  return <TimelineIndicator icon={Flag} text={text || undefined} />;
}

export function TimelineEnd({
  reviewTo,
  publishedAt,
}: {
  reviewTo?: string | null;
  publishedAt?: string | null;
}) {
  const { t } = useTranslation();

  let text = "";
  if (publishedAt) {
    text = `${t(($) => $.commons.workflow.published)} ${formatDate(publishedAt, "date")}`;
  } else if (reviewTo) {
    text = `${t(($) => $.commons.document.reviewTo)} ${formatDate(reviewTo, "date")}`;
  }

  return <TimelineIndicator icon={CheckCircle} text={text} />;
}

type StepIndicatorProps = {
  value?: string;
  status: WorkflowStepStatus;
};

export function StepIndicator({ value, status }: StepIndicatorProps) {
  const StatusIcon = {
    [WorkflowStepStatuses.completed]: Check,
    [WorkflowStepStatuses.active]: ArrowRight,
    [WorkflowStepStatuses.upcoming]: Clock,
  }[status];

  return (
    <div
      className={cn(
        "z-10 flex size-8 shrink-0 items-center justify-center justify-self-center rounded-full font-medium shadow-md",
        {
          "bg-success text-primary-foreground":
            status === WorkflowStepStatuses.completed,
          "bg-info text-primary-foreground":
            status === WorkflowStepStatuses.active,
          "border-border/50 bg-secondary text-muted-foreground border shadow-xs":
            status === WorkflowStepStatuses.upcoming,
        },
      )}
    >
      {value ?? <StatusIcon className="size-4" />}
    </div>
  );
}
